import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";
import contactsService from "../../../../../Services/contactsService";

export function ImportContactsModal({ isOpen, onClose, onImport, existingContacts = [], onOpenValidationPopup, showAlert }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
    }
  };

  const processFile = async (fileToProcess) => {
    const file = fileToProcess || selectedFile;
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      if (showAlert) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_IncorrectFileType, "warning");
      }
      setSelectedFile(null);
      return;
    }

    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const rawHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
      const headerStrings = Array.isArray(rawHeaders) ? rawHeaders.map((h) => String(h).trim().toLowerCase()) : [];
      const isGroupsTemplate = headerStrings.some((h) => ["group name", "group email", "group_email", "distribution list"].includes(h));
      const isDomainsTemplate = headerStrings.some((h) => ["domain", "domains", "domain name", "allowed domains"].includes(h)) && !headerStrings.includes("email");
      const hasContactEmailHeader = headerStrings.some((h) => ["email", "email address", "contact email", "mail", "e-mail"].includes(h));
      const hasContactNameHeader = headerStrings.some((h) => ["first name", "last name", "firstname", "lastname"].includes(h));
      const isValidContactFile = (hasContactEmailHeader || hasContactNameHeader) && !isGroupsTemplate && !isDomainsTemplate;

      if (!isValidContactFile) {
        if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidFile, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      if (!rows || rows.length === 0) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({
            missingRequiredFields: [{ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCannotBeEmpty }]
          });
        } else if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCannotBeEmpty, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const existingEmailSet = new Set((existingContacts || []).map((c) => (c.email || c.Email || c.contactEmail || c.readerEmail || c.mail || "").toLowerCase().trim()).filter(Boolean));
      const existingMobileSet = new Set((existingContacts || []).map((c) => (c.mobile || c.Mobile || c.phone || "").replace(/[^0-9]/g, "")).filter(Boolean));
      const seenEmailsInFile = new Set();
      const seenMobilesInFile = new Set();
      const validList = [];
      let duplicateFound = false;
      const emailLeanghtValidation = [];
      const localpartValiadation = [];
      const longDomainEmails = [];
      const invalidEmailsWithoutDotCom = [];
      const longFirstName = [];
      const longLastName = [];
      const longReference = [];
      const invalidMobileNumbers = [];
      const unauthorizedCountryCodes = [];
      const duplicateInFile = [];
      const duplicateMobileNumbers = [];
      const missingRequiredFields = [];
      const emailRegex = /^[\w.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
      let totalEmailsCountInFile = 0;

      const validDialCodesSet = new Set();
      try {
        const codesData = await contactsService.getCountryCodes();
        if (Array.isArray(codesData) && codesData.length > 0) {
          codesData.forEach((item) => {
            if (item.dialCode) validDialCodesSet.add(item.dialCode.trim());
          });
        }
      } catch (e) {
        console.error("Error fetching country codes:", e);
      }

      rows.forEach((row) => {
        const findField = (...names) => {
          for (const n of names) {
            const match = Object.keys(row).find((k) => {
              if (!k) return false;
              const cleanK = k.trim().toLowerCase();
              const cleanN = n.toLowerCase();
              return (
                cleanK === cleanN ||
                cleanK.replace(/^\+/, "").trim() === cleanN.replace(/^\+/, "").trim() ||
                cleanK.replace(/\s+/g, "_") === cleanN.replace(/\s+/g, "_")
              );
            });
            if (match && row[match] !== undefined && row[match] !== null && String(row[match]).trim() !== "") {
              return String(row[match]).trim();
            }
          }
          return "";
        };

        const email = findField("email", "email address", "mail", "contact email", "e-mail", "email addr", "email_address", "emailaddress");
        const firstName = findField("first name", "firstname", "first", "fname", "first_name");
        const lastName = findField("last name", "lastname", "last", "lname", "last_name");
        let phoneCode = findField("+code", "+ code", "phone code", "phonecode", "country code", "countrycode", "code", "+countrycode", "+country code", "phone_code", "country_code", "mobile country code");
        const mobile = findField("mobile", "phone", "mobile number", "cell", "mobile_number", "mobilenumber", "mobile numb", "mobile_numb", "phone number", "phonenumber");
        const referenceTag = findField("reference", "reference tag", "tag", "notes", "reference_tag", "referencetag");
        if (!email && !firstName && !lastName) return;
        totalEmailsCountInFile++;
        if (!email) {
          missingRequiredFields.push({ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCannotBeEmpty || "Email Address cannot be empty." });
          return;
        }
        const lowerEmail = email.toLowerCase().trim();
        let hasMistake = false;
        if (lowerEmail.length > 255) {
          emailLeanghtValidation.push({ email: lowerEmail });
          hasMistake = true;
        }

        const emailParts = lowerEmail.split("@");
        if (emailParts.length === 2) {
          if (emailParts[0].length > 64) {
            localpartValiadation.push({ email: lowerEmail });
            hasMistake = true;
          }
          if (emailParts[1].length > 190) {
            longDomainEmails.push({ email: lowerEmail });
            hasMistake = true;
          }
        }
        if (!emailRegex.test(lowerEmail)) {
          invalidEmailsWithoutDotCom.push({ email: lowerEmail });
          hasMistake = true;
        }
        if (firstName.length > 50) {
          longFirstName.push({ firstName });
          hasMistake = true;
        }
        if (lastName.length > 50) {
          longLastName.push({ lastName });
          hasMistake = true;
        }
        if (referenceTag.length > 50) {
          longReference.push({ reference: referenceTag });
          hasMistake = true;
        }
        let cleanMobile = mobile.replace(/[^0-9]/g, "");
        if (mobile && (!cleanMobile || cleanMobile.length > 15)) {
          invalidMobileNumbers.push({ mobileNumber: mobile });
          hasMistake = true;
        }

        let rawPhoneCode = (phoneCode || "").trim();
        let formattedPhoneCode = rawPhoneCode;
        if (formattedPhoneCode.includes("-")) {
          formattedPhoneCode = formattedPhoneCode.split("-")[0].trim();
        }
        if (formattedPhoneCode && !formattedPhoneCode.startsWith("+")) {
          formattedPhoneCode = `+${formattedPhoneCode}`;
        }

        if (rawPhoneCode !== "") {
          let isValidCC = true;
          if (validDialCodesSet.size > 0) {
            if (!validDialCodesSet.has(formattedPhoneCode)) {
              isValidCC = false;
            }
          } else {
            if (!/^\+[1-9]\d{0,4}$/.test(formattedPhoneCode)) {
              isValidCC = false;
            }
          }
          if (!isValidCC) {
            unauthorizedCountryCodes.push({ mobileCountryCode: rawPhoneCode });
            hasMistake = true;
          }
        }

        if (seenEmailsInFile.has(lowerEmail)) {
          duplicateInFile.push(lowerEmail);
          hasMistake = true;
        } else {
          seenEmailsInFile.add(lowerEmail);
        }

        if (existingEmailSet.has(lowerEmail)) {
          duplicateFound = true;
        }

        if (cleanMobile) {
          if (seenMobilesInFile.has(cleanMobile)) {
            duplicateMobileNumbers.push({ mobileNumber: mobile });
            hasMistake = true;
          } else {
            seenMobilesInFile.add(cleanMobile);
          }
          if (existingMobileSet.has(cleanMobile)) {
            duplicateFound = true;
          }
        }

        if (hasMistake) return;

        validList.push({
          email: lowerEmail,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneCode: formattedPhoneCode || "+1",
          mobile: cleanMobile,
          referenceTag: referenceTag.trim()
        });
      });

      if (totalEmailsCountInFile === 0) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({
            missingRequiredFields: [{ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCannotBeEmpty }]
          });
        } else if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCannotBeEmpty, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const hasAnyMistakes = emailLeanghtValidation.length > 0 || localpartValiadation.length > 0 || longDomainEmails.length > 0 || invalidEmailsWithoutDotCom.length > 0 || longFirstName.length > 0 || longLastName.length > 0 || longReference.length > 0 || invalidMobileNumbers.length > 0 || unauthorizedCountryCodes.length > 0 || duplicateInFile.length > 0 || duplicateMobileNumbers.length > 0 || missingRequiredFields.length > 0;
      if (hasAnyMistakes) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({ emailLeanghtValidation, localpartValiadation, longDomainEmails, invalidEmailsWithoutDotCom, longFirstName, longLastName, longReference, invalidMobileNumbers, unauthorizedCountryCodes, duplicateInFile, duplicateMobileNumbers, missingRequiredFields });
        }
        setIsProcessing(false);
        return;
      }

      onClose();
      if (onImport) {
        onImport(validList, duplicateFound);
      }
    } catch (err) {
      console.error("Error parsing file:", err);
      if (showAlert) {
        showAlert("Error parsing file.", "warning");
      }
      setSelectedFile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadSample = (format = "csv") => {
    const rawHeaders = [
      selectedLanguage?.LstLanguageCodes?.UI_EmailAdress,
      selectedLanguage?.LstLanguageCodes?.UI_FirstName,
      selectedLanguage?.LstLanguageCodes?.UI_LastName,
      selectedLanguage?.LstLanguageCodes?.UI_CountryCode,
      selectedLanguage?.LstLanguageCodes?.UI_MobileNumber,
      selectedLanguage?.LstLanguageCodes?.UI_ReferenceTxt,
    ];

    if (format === "xlsx") {
      const ws = XLSX.utils.aoa_to_sheet([rawHeaders]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts_Template");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      saveAs(blob, "Contacts_Template.xlsx");
    } else {
      const csvHeaders = rawHeaders.map((h) => (h.startsWith("+") ? `\t${h}` : h));
      const ws = XLSX.utils.aoa_to_sheet([csvHeaders]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts_Template");
      const wbout = XLSX.write(wb, { bookType: "csv", type: "array" });
      const blob = new Blob([wbout], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "Contacts_Template.csv");
    }
  };

  const handleConfirmImport = () => {
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  return (
    <div className="modalCus contacts-modal-show contacts-modal-wrapper">
      <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-import">
        <div className="modal-content border-r4">
          {/* Header */}
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-danger fw-bold font-20">
              {selectedLanguage?.LstLanguageCodes?.UI_ImportReaders || "Import Contacts"}
            </h4>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download" onClick={onClose}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Body */}
          <div className="modal-body p-3">
            {/* Download Sample XLS / CSV Bar */}
            <div className="d-flex align-items-center mb-3">
              <span className="font-14 font-w500 text-dark">
                {selectedLanguage?.LstLanguageCodes?.UI_PleaseDownloadTheXLSorCSV}
              </span>
              <img src={"assets/img/icons/CSVdownload.png"} className="contacts-sample-download-img ms-3" alt="Download CSV" title="Download sample CSV" onClick={() => handleDownloadSample("csv")} />
              <img src={"assets/img/icons/XLSdownload.png"} className="contacts-sample-download-img ms-2" alt="Download XLS" title="Download sample XLS" onClick={() => handleDownloadSample("xlsx")} />
            </div>

            {/* Drag and Drop Zone */}
            <div className={`drag upload form-control contacts-dropzone ${dragActive ? "active-drop" : ""}`} onDragEnter={handleDrag}
              onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
              <div className="d-flex flex-column align-items-center justify-content-center p-3 text-center">
                <input type="file" ref={fileInputRef} className="contacts-hidden-file-input" accept=".csv,.xlsx,.xls" onChange={handleFileInputChange} />
                <span className="material-icons font-32 text-danger mb-1">
                  cloud_upload
                </span>
                <span className="font-14 font-w500 text-dark pb-1">
                  {selectedFile ? selectedFile.name : selectedLanguage?.LstLanguageCodes?.UI_DragandDropfilesHere}
                </span>
                <span className="text-muted font-12 pb-1">
                  {selectedLanguage?.LstLanguageCodes?.UI_or || "or"}
                </span>
                <span className="text-danger underline font-13 font-w600">
                  {selectedLanguage?.LstLanguageCodes?.UI_ClickAndSelectFiles}
                </span>
              </div>
            </div>

            {isProcessing && (
              <div className="text-center py-2 text-secondary font-13">
                Processing file, please wait...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer d-flex align-items-center justify-content-end">
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Cancel} className="btn btn-danger px-2 me-2 ms-1 w-140p truncate" onClick={onClose}>
              {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
            </button>
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Import} className="btn btn-primary px-2 ms-1 w-140p truncate" disabled={!selectedFile || isProcessing} onClick={handleConfirmImport}>
              {selectedLanguage?.LstLanguageCodes?.UI_Import}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportContactsModal;